import React from 'react'
import type { TwitterResult, TwitterPost } from '@creanizer/types'
import { MessageCircle, Repeat2, Heart, Share, MoreHorizontal, CheckCircle2, Reply, CornerDownRight } from 'lucide-react'

interface TwitterViewerProps {
  document: TwitterResult
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
}

export function TwitterViewer({ document, imageAssets }: TwitterViewerProps) {
  const posts = document.posts || []
  
  
  // メインユーザーを特定（最も投稿数が多いユーザー）
  const authorCounts = posts.reduce((acc, post) => {
    const username = post.author.username
    acc[username] = (acc[username] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  
  const mainUser = Object.entries(authorCounts)
    .sort(([, a], [, b]) => b - a)[0]?.[0]
  
  // 投稿をメイン投稿とリプライに分類（replyToがないものがメイン投稿）
  const mainPosts = posts.filter(post => !post.replyTo)
  const replies = posts.filter(post => post.replyTo)

  // リプライをツリー構造に整理
  const organizeReplies = (parentId: string): TwitterPost[] => {
    return replies
      .filter(reply => reply.replyTo?.postId === parentId)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  }

  // 表示されているリプライを追跡（孤立したリプライグループの検出用）
  const displayedReplyIds = new Set<string>()
  const trackDisplayedReplies = (parentId: string, visited: Set<string> = new Set()) => {
    if (visited.has(parentId)) return // 循環参照防止
    visited.add(parentId)

    const directReplies = organizeReplies(parentId)
    directReplies.forEach(reply => {
      displayedReplyIds.add(reply.id)
      trackDisplayedReplies(reply.id, visited)
    })
  }
  mainPosts.forEach(post => trackDisplayedReplies(post.id))

  // 表示されていないリプライ（孤立したリプライ）をグループ化
  const notDisplayedReplies = replies.filter(r => !displayedReplyIds.has(r.id))
  const orphanedGroups: TwitterPost[][] = []
  const processedOrphans = new Set<string>()

  const collectOrphanGroup = (startReply: TwitterPost): TwitterPost[] => {
    const group: TwitterPost[] = []
    const visited = new Set<string>()

    const traverse = (replyId: string) => {
      if (visited.has(replyId) || displayedReplyIds.has(replyId)) return
      visited.add(replyId)

      const reply = replies.find(r => r.id === replyId)
      if (!reply) return

      group.push(reply)
      processedOrphans.add(replyId)

      // 子リプライを探す
      organizeReplies(replyId).forEach(child => {
        if (!displayedReplyIds.has(child.id)) {
          traverse(child.id)
        }
      })

      // 親リプライも探す（逆方向チェーン対応）
      if (reply.replyTo?.postId && !displayedReplyIds.has(reply.replyTo.postId)) {
        traverse(reply.replyTo.postId)
      }
    }

    traverse(startReply.id)
    return group
  }

  // 各孤立リプライからグループを構築
  notDisplayedReplies.forEach(orphan => {
    if (!processedOrphans.has(orphan.id)) {
      const group = collectOrphanGroup(orphan)
      if (group.length > 0) {
        orphanedGroups.push(group)
      }
    }
  })

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* タイムライン情報 */}
      {document.timeline && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          <h3 className="font-bold text-gray-900 mb-2">{document.timeline.mainTopic}</h3>
          <p className="text-sm text-gray-600">{document.timeline.conversationFlow}</p>
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span>{document.timeline.totalPosts} 投稿</span>
            <span>{document.timeline.uniqueAuthors} ユーザー</span>
          </div>
        </div>
      )}
      
      {/* メイン投稿とそのリプライチェーン */}
      {mainPosts.map((post) => (
        <TwitterThread
          key={post.id}
          post={post}
          mainUser={mainUser}
          posts={posts}
          organizeReplies={organizeReplies}
          imageAssets={imageAssets}
          document={document}
        />
      ))}

      {/* 孤立したリプライチェーン（メイン投稿から辿れないスレッド） */}
      {orphanedGroups.length > 0 && (
        <div className="mt-8 pt-6 border-t-2 border-dashed border-yellow-400">
          <div className="bg-yellow-50 border border-yellow-400 rounded-lg p-4 mb-4">
            <h3 className="font-bold text-yellow-900 text-sm mb-2">
              ⚠️ 孤立したスレッド ({orphanedGroups.length}グループ)
            </h3>
            <p className="text-xs text-yellow-800">
              以下のリプライはメイン投稿から繋がっていません（データの問題により正常なツリー構造で表示できません）
            </p>
          </div>

          {orphanedGroups.map((group, groupIndex) => {
            // グループ内の投稿を時系列順にソート
            const sortedGroup = [...group].sort((a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            )

            return (
              <div key={`orphan-group-${groupIndex}`} className="space-y-2 mb-6 bg-yellow-50/30 p-4 rounded-lg border border-yellow-200">
                <div className="text-xs text-yellow-800 mb-2">
                  グループ #{groupIndex + 1} ({group.length}個の投稿)
                </div>
                {sortedGroup.map((orphan) => (
                  <div key={orphan.id} className="ml-8 space-y-2">
                    <div className="relative">
                      {/* 接続線 */}
                      <div className="absolute -left-6 top-0 w-6 h-16">
                        <div className="absolute left-0 top-8 w-5 h-1 rounded-full bg-yellow-300" />
                        <div className="absolute left-0 -top-4 w-1 h-12 rounded-full bg-yellow-300" />
                        <div className="absolute -left-1 top-7 w-3 h-3 rounded-full border-2 border-white bg-yellow-400" />
                      </div>

                      <TwitterPostComponent
                        post={orphan}
                        isMainUser={orphan.author.username === mainUser}
                        isMainPost={false}
                        posts={posts}
                        imageAssets={imageAssets}
                        document={document}
                      />

                      {/* リプライ先情報を表示 */}
                      {orphan.replyTo && (
                        <div className="mt-1 text-xs text-yellow-700">
                          → 返信先: {orphan.replyTo.postId}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface TwitterThreadProps {
  post: TwitterPost
  mainUser: string
  posts: TwitterPost[]
  organizeReplies: (parentId: string) => TwitterPost[]
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
  document?: TwitterResult
}

function TwitterThread({ post, mainUser, posts, organizeReplies, imageAssets, document }: TwitterThreadProps) {
  return (
    <div className="space-y-2">
      <TwitterPostComponent
        post={post}
        isMainUser={post.author.username === mainUser}
        isMainPost={true}
        posts={posts}
        imageAssets={imageAssets}
        document={document}
      />

      <ReplyThread
        parentId={post.id}
        organizeReplies={organizeReplies}
        mainUser={mainUser}
        posts={posts}
        imageAssets={imageAssets}
        document={document}
        depth={0}
        maxDepth={50}
      />
    </div>
  )
}

interface ReplyThreadProps {
  parentId: string
  organizeReplies: (parentId: string) => TwitterPost[]
  mainUser: string
  posts: TwitterPost[]
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
  document?: TwitterResult
  depth: number
  maxDepth: number
}

function ReplyThread({
  parentId,
  organizeReplies,
  mainUser,
  posts,
  imageAssets,
  document,
  depth,
  maxDepth
}: ReplyThreadProps) {
  const replies = organizeReplies(parentId)

  if (replies.length === 0 || depth >= maxDepth) {
    return null
  }

  return (
    <>
      {replies.map((reply) => (
        <div key={reply.id} className="ml-8 space-y-2">
          <div className="relative">
            {/* 接続線 */}
            <div className="absolute -left-6 top-0 w-6 h-16">
              <div className={`absolute left-0 top-8 w-5 h-1 rounded-full ${depth === 0 ? 'bg-blue-300' : 'bg-gray-300'}`} />
              <div className={`absolute left-0 -top-4 w-1 h-12 rounded-full ${depth === 0 ? 'bg-blue-300' : 'bg-gray-300'}`} />
              <div className={`absolute -left-1 top-7 w-3 h-3 rounded-full border-2 border-white ${depth === 0 ? 'bg-blue-400' : 'bg-gray-400'}`} />
            </div>

            <TwitterPostComponent
              post={reply}
              isMainUser={reply.author.username === mainUser}
              isMainPost={false}
              posts={posts}
              imageAssets={imageAssets}
              document={document}
            />
          </div>

          {/* 再帰的にさらにリプライを表示 */}
          <ReplyThread
            parentId={reply.id}
            organizeReplies={organizeReplies}
            mainUser={mainUser}
            posts={posts}
            imageAssets={imageAssets}
            document={document}
            depth={depth + 1}
            maxDepth={maxDepth}
          />
        </div>
      ))}
    </>
  )
}

interface TwitterPostComponentProps {
  post: TwitterPost
  isMainUser: boolean
  isMainPost: boolean
  posts: TwitterPost[]
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
  document?: TwitterResult
}

function TwitterPostComponent({ post, isMainUser, isMainPost, posts, imageAssets, document }: TwitterPostComponentProps) {
  // imageMappingからアバター画像を取得
  const getAvatarForUser = (username: string): string | undefined => {
    if (!imageAssets?.avatars) return undefined
    
    // documentのimageMappingを参照
    const imageMapping = document?.imageMapping || {}
    
    // usernameは@を含む（例: @yamada_taro）
    const originalName = imageMapping[username]
    
    if (originalName && imageAssets.avatars[originalName]) {
      return imageAssets.avatars[originalName]
    }
    
    // @を除いた形でも試す
    const usernameWithoutAt = username.startsWith('@') ? username.slice(1) : username
    const altName = imageMapping[usernameWithoutAt] || imageMapping[`@${usernameWithoutAt}`]
    if (altName && imageAssets.avatars[altName]) {
      return imageAssets.avatars[altName]
    }
    
    // フォールバック：直接マッチング
    return imageAssets.avatars[username] || imageAssets.avatars[usernameWithoutAt]
  }
  
  // 数値フォーマット
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }
  
  // 時間フォーマット
  const formatTime = (timestamp: string): string => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now.getTime() - date.getTime()
      const hours = Math.floor(diff / (1000 * 60 * 60))
      
      if (hours < 1) return '今'
      if (hours < 24) return `${hours}時間前`
      if (hours < 24 * 7) return `${Math.floor(hours / 24)}日前`
      
      return date.toLocaleDateString('ja-JP', {
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return '不明'
    }
  }
  
  // テキストフォーマット（ハッシュタグとメンション）
  const formatText = (text: string, hashtags?: string[], mentions?: string[]): React.ReactNode => {
    let formattedText = text
    
    // ハッシュタグを強調
    hashtags?.forEach(tag => {
      const regex = new RegExp(`#${tag}\\b`, 'gi')
      formattedText = formattedText.replace(regex, `<span class="text-blue-500">#${tag}</span>`)
    })
    
    // メンションを強調
    mentions?.forEach(mention => {
      const regex = new RegExp(`@${mention}\\b`, 'gi')
      formattedText = formattedText.replace(regex, `<span class="text-blue-500">@${mention}</span>`)
    })
    
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />
  }
  
  return (
    <div className={`
      ${isMainPost 
        ? 'bg-white border-2 border-blue-200 shadow-sm' 
        : 'bg-gray-50 border border-gray-200'
      } 
      rounded-2xl p-4 hover:bg-gray-50 transition-colors relative
    `}>
      {/* リプライインジケーター */}
      {!isMainPost && (
        <div className="absolute -top-2 left-6 bg-gray-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <Reply className="w-3 h-3" />
          <span>返信</span>
        </div>
      )}
      
      {/* メイン投稿インジケーター（replyToがない投稿のみ） */}
      {isMainPost && !post.replyTo && (
        <div className="absolute -top-3 -right-2">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
            メイン投稿
          </div>
        </div>
      )}
      
      {/* ヘッダー */}
      <div className="flex items-start gap-3">
        {/* アバター */}
        {getAvatarForUser(post.author.username) ? (
          <img
            src={getAvatarForUser(post.author.username)!}
            alt={post.author.displayName}
            className={`
              ${isMainPost ? 'w-12 h-12' : 'w-10 h-10'}
              rounded-full object-cover border-2
              ${isMainUser ? 'border-blue-400' : 'border-gray-300'}
            `}
          />
        ) : (
          <div className={`
            ${isMainPost ? 'w-12 h-12' : 'w-10 h-10'}
            ${isMainUser 
              ? 'bg-gradient-to-br from-blue-400 to-blue-600' 
              : 'bg-gradient-to-br from-gray-400 to-gray-600'
            }
            rounded-full flex items-center justify-center text-white font-bold
          `}>
            {post.author.displayName.charAt(0).toUpperCase()}
          </div>
        )}
        
        <div className="flex-1">
          {/* ユーザー情報 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 flex-wrap">
              <span className={`font-bold ${isMainPost ? 'text-lg' : 'text-base'} text-gray-900`}>
                {post.author.displayName}
              </span>
              {post.author.verified && (
                <CheckCircle2 className="w-5 h-5 text-blue-500 fill-current" />
              )}
              <span className="text-gray-500">@{post.author.username}</span>
              <span className="text-gray-500">·</span>
              <span className="text-gray-500">{formatTime(post.timestamp)}</span>
              {isMainUser && !isMainPost && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full ml-2">
                  投稿者
                </span>
              )}
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>
          
          {/* リプライ先 */}
          {post.replyTo && (
            <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <CornerDownRight className="w-4 h-4" />
              <span>返信先: <span className="text-blue-500 font-medium">@{post.replyTo.username}</span></span>
            </div>
          )}
          
          {/* スレッド表示 */}
          {post.isThread && (
            <div className="text-sm text-gray-500 mt-1">
              スレッド {post.threadPosition}/{posts.filter(p => p.isThread).length}
            </div>
          )}
          
          {/* 本文 */}
          <div className={`mt-2 ${isMainPost ? 'text-base font-medium' : 'text-[15px]'} leading-normal text-gray-900 whitespace-pre-wrap`}>
            {formatText(post.text, post.hashtags, post.mentions)}
          </div>
          
          {/* 引用ツイート */}
          {post.quoteTweet && (
            <div className="mt-3 border border-gray-200 rounded-2xl p-3">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 bg-gray-300 rounded-full" />
                <span className="font-semibold text-gray-900">{post.quoteTweet.displayName}</span>
                <span className="text-gray-500">@{post.quoteTweet.username}</span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-500">{formatTime(post.quoteTweet.timestamp)}</span>
              </div>
              <div className="mt-2 text-sm text-gray-700">
                {post.quoteTweet.text}
              </div>
            </div>
          )}
          
          {/* メディア */}
          {post.media && post.media.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {post.media.map((media, index) => (
                <div key={index} className="bg-gray-200 rounded-2xl p-8 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl mb-2">
                      {media.type === 'image' ? '🖼️' : media.type === 'video' ? '🎥' : '🎬'}
                    </div>
                    <p className="text-sm text-gray-600">{media.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* エンゲージメント */}
          <div className="flex items-center justify-between mt-4 -ml-2">
            <button className="flex items-center gap-1.5 text-gray-500 hover:text-blue-500 transition-colors p-2 hover:bg-blue-50 rounded-full">
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm">{formatNumber(post.engagement.replies)}</span>
            </button>
            
            <button className="flex items-center gap-1.5 text-gray-500 hover:text-green-500 transition-colors p-2 hover:bg-green-50 rounded-full">
              <Repeat2 className="w-5 h-5" />
              <span className="text-sm">{formatNumber(post.engagement.retweets)}</span>
            </button>
            
            <button className="flex items-center gap-1.5 text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-full">
              <Heart className="w-5 h-5" />
              <span className="text-sm">{formatNumber(post.engagement.likes)}</span>
            </button>
            
            <button className="text-gray-500 hover:text-blue-500 transition-colors p-2 hover:bg-blue-50 rounded-full">
              <Share className="w-5 h-5" />
            </button>
          </div>
          
          {/* 表示回数とフォロワー数 */}
          {isMainPost && (
            <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100 flex items-center gap-4">
              {post.engagement.views && (
                <span>{formatNumber(post.engagement.views)} 表示</span>
              )}
              {post.author.followers && (
                <span>{post.author.followers} フォロワー</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}